import { Outlet, Link, useParams, useLocation, Navigate } from 'react-router-dom'
import { Home, Ticket, Box, BookOpen, AlertCircle } from 'lucide-react'
import { usePortalMeQuery } from './customer-portal-api'
import { Skeleton } from '@/components/ui/misc'

export function PortalShell() {
  const { token } = useParams<{ token: string }>()
  const location = useLocation()

  if (!token) return <Navigate to="/" replace />

  const { data, isLoading, isError, error } = usePortalMeQuery(token)
  const status = (error as { status?: number })?.status

  if (isError) {
    return <PortalError status={status} />
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="border-b border-border bg-background">
          <div className="mx-auto max-w-6xl px-4 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </div>
        <div className="mx-auto max-w-6xl space-y-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  const { customer, session } = data.data
  const expiresAt = new Date(session.expires_at)
  const daysLeft = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))

  const navItems = [
    { path: '', label: 'Overview', icon: Home },
    { path: 'tickets', label: 'Tickets', icon: Ticket },
    { path: 'assets', label: 'Assets', icon: Box },
    { path: 'kb', label: 'Knowledge Base', icon: BookOpen },
  ]

  function isActive(path: string): boolean {
    const base = `/portal/${token}`
    if (path === '') return location.pathname === base || location.pathname === `${base}/`
    return location.pathname.startsWith(`${base}/${path}`)
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Customer Portal</p>
              <h1 className="font-display text-xl font-semibold">{customer.name}</h1>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>Session: {session.label}</p>
              <p>Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="mx-auto max-w-6xl px-4">
            <nav className="flex gap-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const active = isActive(item.path)
                return (
                  <Link
                    key={item.path}
                    to={item.path ? `/portal/${token}/${item.path}` : `/portal/${token}`}
                    className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? 'border-accent font-medium text-accent'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl p-4">
        <Outlet />
      </div>

      {/* Footer */}
      <div className="mx-auto max-w-6xl px-4 py-6 text-center text-[10px] text-muted-foreground">
        <p>This is a private portal. Do not share this link.</p>
      </div>
    </div>
  )
}

function PortalError({ status }: { status?: number }) {
  let title = 'Portal unavailable'
  let message = 'Something went wrong. Please contact the company that sent you this link.'

  if (status === 404) {
    title = 'Invalid link'
    message = 'This portal link is not valid. The URL may be incorrect, or the session has been revoked.'
  } else if (status === 410) {
    title = 'Link expired or revoked'
    message = 'This portal session has expired or been revoked. Contact the company for a new link.'
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="max-w-md rounded-md border border-border bg-background p-6 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-amber-600" />
        <h1 className="mt-4 font-display text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  )
}
